/**
 * The authoritative vote state the participate endpoints return, and a validating parser for it.
 *
 * Both `PUT /ajax/discussion/participate/{id}/{mode}` and `DELETE /ajax/discussion/participate/{id}`
 * answer with this shape, read back by the server AFTER the write transaction commits. Rendering it
 * instead of an optimistic `count + 1` is what makes a client/server desync self-heal on the next
 * vote rather than persist until the user reloads.
 *
 * The parser is deliberately total and defensive rather than a cast. The generated client still types
 * these responses as `object` / `void` (the endpoints returned no body until 2026-08-06), and more
 * importantly a deploy can put a frontend in front of a backend that has not shipped the body yet.
 * `null` means "no usable server state" and every caller must fall back to its optimistic update —
 * a missing body must never degrade to "voting does nothing".
 */
export type DiscussionVoteState = {
    agreementsCount: number;
    disagreementsCount: number;
    viewerAgreeing: boolean;
    viewerDisagreeing: boolean;
};

function readCount(value: unknown): number | null {
    return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : null;
}

export function parseDiscussionVoteState(raw: unknown): DiscussionVoteState | null {
    if (!raw || typeof raw !== "object") {
        return null;
    }
    const candidate = raw as Record<string, unknown>;
    const agreementsCount = readCount(candidate.agreementsCount);
    const disagreementsCount = readCount(candidate.disagreementsCount);
    if (agreementsCount === null || disagreementsCount === null) {
        return null;
    }
    if (typeof candidate.viewerAgreeing !== "boolean" || typeof candidate.viewerDisagreeing !== "boolean") {
        return null;
    }
    // The server cannot hold both at once (one row, one mode). If it somehow says so, the payload is
    // not trustworthy enough to overwrite good local state with.
    if (candidate.viewerAgreeing && candidate.viewerDisagreeing) {
        return null;
    }
    return {
        agreementsCount,
        disagreementsCount,
        viewerAgreeing: candidate.viewerAgreeing,
        viewerDisagreeing: candidate.viewerDisagreeing,
    };
}

/**
 * The state a vote in `mode` produces, applied to `current` — the OPTIMISTIC prediction, used only
 * when the server returned no usable body (see the parser's contract above).
 *
 * It lives here, once, because two components render this control (`DiscussionButtons` on cards and
 * the discussion page, `DiscussionVoteButtons` in the recommendation rail) and each used to carry its
 * own copy of the ±1 arithmetic. Two copies of "withdraw the opposite vote at the same time" is one
 * copy too many, and the drift would be invisible: both render a plausible number.
 *
 * Toggle semantics, unchanged from what the two copies did: pressing the vote you already hold
 * withdraws it; pressing the other one switches, withdrawing the first in the same move.
 */
export function applyVoteToState(current: DiscussionVoteState, mode: "AGREE" | "DISAGREE"): DiscussionVoteState {
    const withdraw = (count: number): number => Math.max(0, count - 1);

    if (mode === "AGREE") {
        if (current.viewerAgreeing) {
            return {
                agreementsCount: withdraw(current.agreementsCount),
                disagreementsCount: current.disagreementsCount,
                viewerAgreeing: false,
                viewerDisagreeing: false,
            };
        }
        return {
            agreementsCount: current.agreementsCount + 1,
            disagreementsCount: current.viewerDisagreeing
                ? withdraw(current.disagreementsCount)
                : current.disagreementsCount,
            viewerAgreeing: true,
            viewerDisagreeing: false,
        };
    }

    if (current.viewerDisagreeing) {
        return {
            agreementsCount: current.agreementsCount,
            disagreementsCount: withdraw(current.disagreementsCount),
            viewerAgreeing: false,
            viewerDisagreeing: false,
        };
    }
    return {
        agreementsCount: current.viewerAgreeing ? withdraw(current.agreementsCount) : current.agreementsCount,
        disagreementsCount: current.disagreementsCount + 1,
        viewerAgreeing: false,
        viewerDisagreeing: true,
    };
}

/** The viewer's vote as the agreement store and the cross-component event express it. */
export function agreementFromVoteState(state: DiscussionVoteState): "AGREE" | "DISAGREE" | undefined {
    if (state.viewerAgreeing) {
        return "AGREE";
    }
    if (state.viewerDisagreeing) {
        return "DISAGREE";
    }
    return undefined;
}
